import api from './axiosConfig';

export type AdminAnalyticsDailyRow = {
  statDate?: string | null;
  mainUv?: number | null;
  guestUv?: number | null;
  memberUv?: number | null;
  stay15Uv?: number | null;
  stay30Uv?: number | null;
  authSuccessUv?: number | null;
  actionDeskUv?: number | null;
  activeUsers?: number | null;
  newUsers?: number | null;
};

export type AdminAnalyticsFunnelRow = {
  sortNo?: number | null;
  code?: string | null;
  label?: string | null;
  users?: number | null;
  ratePct?: number | null;
};

export type AdminAnalyticsBehaviorRow = {
  eventType?: string | null;
  eventCount?: number | null;
  uv?: number | null;
  userCount?: number | null;
};

export type AdminAnalyticsSourceRow = {
  source?: string | null;
  /** canonical people attributed to this first-touch source */
  uv?: number | null;
  sharePct?: number | null;
  /** sessions whose session acquisition source resolves here */
  sessionCount?: number | null;
  sessionSharePct?: number | null;
};

export type AdminAnalyticsRouteRow = {
  path?: string | null;
  views?: number | null;
  uv?: number | null;
};

export type AdminAnalyticsVisitorRow = {
  visitorKey?: string | null;
  userId?: number | null;
  email?: string | null;
  userType?: 'GUEST' | 'MEMBER' | string | null;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  eventCount?: number | null;
  pageViews?: number | null;
  mainViews?: number | null;
  visibleSeconds?: number | null;
  /** MEASURED = visible main_exit authority, LOWER_BOUND = heartbeat/milestone only, UNKNOWN = no lifecycle evidence */
  engagementStatus?: 'MEASURED' | 'LOWER_BOUND' | 'UNKNOWN' | string | null;
  visibleLowerBound?: boolean | number | null;
  /** number of raw anonymous identities stitched into this canonical person */
  sourceAnonCount?: number | null;
  maxScrollPct?: number | null;
  stayed30s?: boolean | number | null;
  protectedInterest?: boolean | number | null;
  loggedIn?: boolean | number | null;
  actionDesk?: boolean | number | null;
  stockDetail?: boolean | number | null;
  watchlistAdded?: boolean | number | null;
  lastPath?: string | null;
};

export type AdminAnalyticsSignupRow = {
  userId?: number | null;
  email?: string | null;
  isActive?: boolean | number | null;
  createdAt?: string | null;
  firstActivityAt?: string | null;
  lastActivityAt?: string | null;
  eventCount?: number | null;
  mainViewed?: boolean | number | null;
  authSuccess?: boolean | number | null;
  actionDesk?: boolean | number | null;
  liveSummaryUsed?: boolean | number | null;
  stockDetail?: boolean | number | null;
  watchlistAdded?: boolean | number | null;
  activatedWithin24h?: boolean | number | null;
};


export type AdminAnalyticsSummary = {
  siteUv?: number | null;
  mainUv?: number | null;
  mainViews?: number | null;
  guestMainUv?: number | null;
  memberMainUv?: number | null;
  guestMemberOverlapUv?: number | null;
  stay5Uv?: number | null;
  stay15Uv?: number | null;
  stay30Uv?: number | null;
  firstClickUv?: number | null;
  protectedInterestUv?: number | null;
  authSuccessUv?: number | null;
  authSuccessUsers?: number | null;
  productActionUv?: number | null;
  trackedStockUv?: number | null;
  judgementViewUv?: number | null;
  changeExperienceUv?: number | null;
  newVisitorUv?: number | null;
  landingViewUv?: number | null;
  avgEngagedSeconds?: number | null;
  medianEngagedSeconds?: number | null;
  p75EngagedSeconds?: number | null;
  mainSessions?: number | null;
  engagementMeasuredSessions?: number | null;
  engagementCoveragePct?: number | null;
  retentionD1Eligible?: number | null;
  retentionD1Returned?: number | null;
  retentionD1RatePct?: number | null;
  retentionD3Eligible?: number | null;
  retentionD3Returned?: number | null;
  retentionD3RatePct?: number | null;
  retentionD7Eligible?: number | null;
  retentionD7Returned?: number | null;
  retentionD7RatePct?: number | null;
};

export type AdminAnalyticsGrowth = {
  totalUsers?: number | null;
  activeAccounts?: number | null;
  newUsersToday?: number | null;
  newUsersPeriod?: number | null;
  newUsersEligibleFor24h?: number | null;
  newUsersActivatedWithin24h?: number | null;
  newUserActivationRatePct?: number | null;
  newUsersPreviousPeriod?: number | null;
  growthRatePct?: number | null;
  activeUsersPeriod?: number | null;
};

export type AdminAnalyticsOverview = {
  periodDays?: number | null;
  generatedAt?: string | null;
  adminExcluded?: boolean | null;
  summary?: AdminAnalyticsSummary | null;
  growth?: AdminAnalyticsGrowth | null;
  daily?: AdminAnalyticsDailyRow[] | null;
  funnel?: AdminAnalyticsFunnelRow[] | null;
  behaviors?: AdminAnalyticsBehaviorRow[] | null;
  sources?: AdminAnalyticsSourceRow[] | null;
  routes?: AdminAnalyticsRouteRow[] | null;
  recentVisitors?: AdminAnalyticsVisitorRow[] | null;
  recentSignups?: AdminAnalyticsSignupRow[] | null;
};

export async function getAdminAnalyticsOverview(days = 30): Promise<AdminAnalyticsOverview> {
  const safeDays = [7, 30, 90].includes(Number(days)) ? Number(days) : 30;
  const response = await api.get<AdminAnalyticsOverview>('/analytics/admin/overview', {
    params: {days: safeDays},
  });
  return response.data ?? {};
}
